import { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { DimensionSchemaConfig, DimensionField, DimensionCategory, DimensionFieldType } from '../types/dimension.types';
import { generateCompleteSchema, validateDimension } from './dimension-utils';

interface DimensionBuilderProps {
  dimension?: DimensionSchemaConfig | null;
  onSave: (dimension: DimensionSchemaConfig) => void;
  onCancel: () => void;
}

const CATEGORIES: { value: DimensionCategory; label: string }[] = [
  { value: 'temporal', label: 'Temporal (Waktu)' },
  { value: 'geographic', label: 'Geographic (Wilayah)' },
  { value: 'government', label: 'Government (Pemerintahan)' },
  { value: 'organizational', label: 'Organizational (Organisasi)' },
  { value: 'demographic', label: 'Demographic (Kependudukan)' },
  { value: 'economic', label: 'Economic (Ekonomi)' },
  { value: 'infrastructure', label: 'Infrastructure (Infrastruktur)' },
  { value: 'custom', label: 'Custom (Kustom)' },
];

const FIELD_TYPES: { value: DimensionFieldType; label: string }[] = [
  { value: 'string', label: 'String (Teks Pendek)' },
  { value: 'text', label: 'Text (Teks Panjang)' },
  { value: 'integer', label: 'Integer (Bilangan Bulat)' },
  { value: 'decimal', label: 'Decimal (Bilangan Desimal)' },
  { value: 'boolean', label: 'Boolean (Benar/Salah)' },
  { value: 'date', label: 'Date (Tanggal)' },
  { value: 'datetime', label: 'DateTime (Tanggal & Waktu)' },
  { value: 'enum', label: 'Enum (Pilihan Tetap)' },
  { value: 'json', label: 'JSON (Data Fleksibel)' },
];

export function DimensionBuilder({ dimension, onSave, onCancel }: DimensionBuilderProps) {
  const isEditing = !!dimension;

  // Form state
  const [formData, setFormData] = useState<DimensionSchemaConfig>(
    dimension || {
      id: '',
      name: '',
      category: 'custom',
      description: '',
      icon: 'Settings',
      isSystem: false,
      fields: [],
    }
  );

  const [validation, setValidation] = useState<{ valid: boolean; errors: string[]; warnings: string[] }>({
    valid: true,
    errors: [],
    warnings: [],
  });

  // Field being edited
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [fieldFormData, setFieldFormData] = useState<DimensionField>({
    name: '',
    type: 'string',
    required: false,
  });

  // Show generated SQL preview
  const [showPreview, setShowPreview] = useState(false);
  const [generatedSchema, setGeneratedSchema] = useState<ReturnType<typeof generateCompleteSchema> | null>(null);

  // Validate on change
  useEffect(() => {
    if (formData.id || formData.name) {
      setValidation(validateDimension(formData));
    }
  }, [formData]);

  // Generate schema preview
  useEffect(() => {
    if (formData.fields.length > 0) {
      try {
        setGeneratedSchema(generateCompleteSchema(formData));
      } catch (e) {
        // Ignore errors during typing
      }
    }
  }, [formData]);

  const handleSaveDimension = () => {
    if (!validation.valid) {
      alert('Please fix validation errors before saving');
      return;
    }
    onSave(formData);
  };

  const handleAddField = () => {
    if (!fieldFormData.name || !fieldFormData.type) {
      alert('Field name and type are required');
      return;
    }

    const newField: DimensionField = {
      ...fieldFormData,
      label: fieldFormData.label || fieldFormData.name,
    };

    setFormData({
      ...formData,
      fields: [...formData.fields, newField],
    });

    // Reset field form
    setFieldFormData({
      name: '',
      type: 'string',
      required: false,
    });
  };

  const handleUpdateField = () => {
    if (editingFieldIndex === null || !fieldFormData.name || !fieldFormData.type) {
      return;
    }

    const updatedField: DimensionField = {
      ...fieldFormData,
      label: fieldFormData.label || fieldFormData.name,
    };

    const updatedFields = [...formData.fields];
    updatedFields[editingFieldIndex] = updatedField;

    setFormData({
      ...formData,
      fields: updatedFields,
    });

    setEditingFieldIndex(null);
    setFieldFormData({ name: '', type: 'string', required: false });
  };

  const handleEditField = (index: number) => {
    setEditingFieldIndex(index);
    setFieldFormData({ ...formData.fields[index] });
  };

  const handleDeleteField = (index: number) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter((_, i) => i !== index),
    });
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    const fields = [...formData.fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= fields.length) return;

    [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];

    setFormData({ ...formData, fields });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            {isEditing ? 'Edit Dimension' : 'New Dimension'}
          </h2>
          <p className="text-muted-foreground">
            {isEditing ? 'Modify dimension configuration' : 'Create a new dimension for your data warehouse'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSaveDimension} disabled={!validation.valid}>
            Save Dimension
          </Button>
        </div>
      </div>

      {/* Validation Errors */}
      {validation.errors.length > 0 && (
        <Card className="border-destructive">
          <CardContent className="pt-4">
            <p className="font-medium text-destructive mb-2">Please fix the following errors:</p>
            <ul className="list-disc list-inside text-sm text-destructive space-y-1">
              {validation.errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Warnings */}
      {validation.warnings.length > 0 && (
        <Card className="border-yellow-500">
          <CardContent className="pt-4">
            <p className="font-medium text-yellow-700 mb-2">Warnings:</p>
            <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
              {validation.warnings.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>Core information about this dimension</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="id">Dimension ID *</Label>
              <Input
                id="id"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="e.g., program, department, region"
                disabled={isEditing}
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier (snake_case)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Display Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Program, Department, Region"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value as DimensionCategory })}
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tableName">Table Name (Optional)</Label>
              <Input
                id="tableName"
                value={formData.tableName || ''}
                onChange={(e) => setFormData({ ...formData, tableName: e.target.value || undefined })}
                placeholder="Auto-generated if empty"
              />
              <p className="text-xs text-muted-foreground">
                Defaults to: dim_{formData.id || 'dimension_name'}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this dimension represents..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Fields */}
      <Card>
        <CardHeader>
          <CardTitle>Fields</CardTitle>
          <CardDescription>Define the columns for this dimension table</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add/Edit Field Form */}
          <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
            <h3 className="font-medium">
              {editingFieldIndex !== null ? 'Edit Field' : 'Add New Field'}
            </h3>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fieldName">Field Name *</Label>
                <Input
                  id="fieldName"
                  value={fieldFormData.name}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="snake_case"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fieldType">Data Type *</Label>
                <Select
                  value={fieldFormData.type}
                  onValueChange={(value) => setFieldFormData({ ...fieldFormData, type: value as DimensionFieldType })}
                >
                  <SelectTrigger id="fieldType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fieldLabel">Display Label</Label>
                <Input
                  id="fieldLabel"
                  value={fieldFormData.label || ''}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, label: e.target.value })}
                  placeholder="Human readable name"
                />
              </div>
            </div>

            {/* Type-specific options */}
            {fieldFormData.type === 'string' && (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fieldLength">Max Length</Label>
                  <Input
                    id="fieldLength"
                    type="number"
                    value={fieldFormData.length || 255}
                    onChange={(e) => setFieldFormData({ ...fieldFormData, length: parseInt(e.target.value) || 255 })}
                  />
                </div>
              </div>
            )}

            {fieldFormData.type === 'decimal' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="precision">Precision</Label>
                  <Input
                    id="precision"
                    type="number"
                    value={fieldFormData.precision || 18}
                    onChange={(e) => setFieldFormData({ ...fieldFormData, precision: parseInt(e.target.value) || 18 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scale">Scale</Label>
                  <Input
                    id="scale"
                    type="number"
                    value={fieldFormData.scale || 2}
                    onChange={(e) => setFieldFormData({ ...fieldFormData, scale: parseInt(e.target.value) || 2 })}
                  />
                </div>
              </div>
            )}

            {fieldFormData.type === 'enum' && (
              <div className="space-y-2">
                <Label htmlFor="enumValues">Enum Values (comma-separated)</Label>
                <Input
                  id="enumValues"
                  value={fieldFormData.values?.join(', ') || ''}
                  onChange={(e) => setFieldFormData({
                    ...fieldFormData,
                    values: e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                  })}
                  placeholder="Option 1, Option 2, Option 3"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="fieldDescription">Description</Label>
              <Input
                id="fieldDescription"
                value={fieldFormData.description || ''}
                onChange={(e) => setFieldFormData({ ...fieldFormData, description: e.target.value })}
                placeholder="Field description for documentation"
              />
            </div>

            <div className="flex gap-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="required"
                  checked={fieldFormData.required || false}
                  onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, required: checked })}
                />
                <Label htmlFor="required">Required</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="unique"
                  checked={fieldFormData.unique || false}
                  onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, unique: checked })}
                />
                <Label htmlFor="unique">Unique</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="indexed"
                  checked={fieldFormData.indexed || false}
                  onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, indexed: checked })}
                />
                <Label htmlFor="indexed">Indexed</Label>
              </div>
            </div>

            <div className="flex gap-2">
              {editingFieldIndex !== null ? (
                <>
                  <Button onClick={handleUpdateField}>Update Field</Button>
                  <Button variant="outline" onClick={() => {
                    setEditingFieldIndex(null);
                    setFieldFormData({ name: '', type: 'string', required: false });
                  }}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button onClick={handleAddField}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Field
                </Button>
              )}
            </div>
          </div>

          {/* Fields List */}
          {formData.fields.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-medium">Defined Fields ({formData.fields.length})</h3>
              <div className="border rounded-lg divide-y">
                {formData.fields.map((field, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleMoveField(index, 'up')}
                          disabled={index === 0}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleMoveField(index, 'down')}
                          disabled={index === formData.fields.length - 1}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                      <div>
                        <p className="font-medium text-sm">{field.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {field.type} {field.length && `(${field.length})`}
                          {field.required && <Badge variant="outline" className="ml-2 text-xs">Required</Badge>}
                          {field.unique && <Badge variant="outline" className="ml-2 text-xs">Unique</Badge>}
                          {field.indexed && <Badge variant="outline" className="ml-2 text-xs">Indexed</Badge>}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEditField(index)}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDeleteField(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generated Schema Preview */}
      {generatedSchema && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Generated Schema</CardTitle>
                <CardDescription>Preview of Prisma schema and SQL that will be created</CardDescription>
              </div>
              <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
                {showPreview ? 'Hide' : 'Show'} Preview
              </Button>
            </div>
          </CardHeader>
          {showPreview && (
            <CardContent className="space-y-4">
              {/* Prisma Schema */}
              <div>
                <h4 className="font-medium mb-2">Prisma Schema</h4>
                <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs max-h-64">
                  {generatedSchema.prismaSchema}
                </pre>
              </div>

              {/* SQL */}
              <div>
                <h4 className="font-medium mb-2">SQL CREATE TABLE</h4>
                <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs max-h-64">
                  {generatedSchema.createTableSQL}
                </pre>
              </div>

              {/* Constraints */}
              {generatedSchema.createIndexSQL.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">SQL Constraints & Indexes</h4>
                  <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs max-h-64">
                    {generatedSchema.createIndexSQL.join('\n')}
                  </pre>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
